import { FC, KeyboardEvent, useEffect, useMemo, useState } from "react";

import Link from "next/link";

import { Button, Dropdown, Input, Layout, theme, Tooltip } from "antd";

import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SearchOutlined,
} from "@ant-design/icons";

import { HiBell, HiChevronDown } from "react-icons/hi";

import useAuthStore from "@/store/useAuthStore";
import useLayoutStore from "@/store/useLayoutStore";

import { useAuthenticator } from "@aws-amplify/ui-react";

import { PAGE_URL } from "@/utils/constants";
import { useRouter } from "next/router";

import Avatar from "@/components/Avatar";
import BrandLogo from "@/components/Sidebar/BrandLogo";

import { useAnalytics } from "@/hooks/useAnalytics";
import useMyActivityStore from "@/hooks/useMyActivitiesStore";
import useGetMyActivity from "@/hooks/useMyActivity";
import { clearSentryUser } from "@/sentry";
import type { MenuProps } from "antd";
import { useTypeTier } from "../Card/card-user-profile";
import { myProfileTabKey } from "../pages/profile/MyProfileTabsContent";
import { useMyActivityV3 } from "@/hooks/useMyActivityV3";

const { Header: HeaderAntd } = Layout;

const HeaderComponent: FC<{ forceRemount: () => void }> = ({
  forceRemount,
}) => {
  const { track } = useAnalytics();
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const router = useRouter();
  const handlerLogout = useAuthStore.useLogout();
  const authUser = useAuthStore.useUser();
  const [avatarUrl, setAvatarUrl] = useState("");

  const title = useLayoutStore.useTitle();
  const rightCta = useLayoutStore.useRightCta();
  const collapsed = useLayoutStore.useCollapsed();
  const setCollapsed = useLayoutStore.useSetCollapsed();

  const { isAuthenticated } = useAuthStore();
  const { fetchMyActivities } =
      useMyActivityV3({});
  
    // Hooks
    useEffect(() => {
      if (isAuthenticated) {
        fetchMyActivities();
      }
    }, [isAuthenticated]);

  const { processedActivities } = useMyActivityStore();
  const isNotificationExist = useMemo(() => {
    const isConnectionRequestExist = processedActivities?.filter(x => x?.context === "connectionActivities")?.some(
      ({ description }) => description?.includes("wants to connect with you")
    );

    const listOfNeedActionActivities = ["championRequests",
      "circleInvitations",
      "circleRecommendations",
      "connectionRecommendations",
      "endorsements",
      "eventInvitations",
      "eventRecommendations",
      "mentorshipRequests",
      "relationshipUpgradeRequests",
      "skillRecommendations",
      "skillVerificationRequests"]

    const filterActivitiesOnNeedAction = processedActivities?.filter(x => listOfNeedActionActivities?.includes(x?.context as string));

    return (
      (filterActivitiesOnNeedAction && filterActivitiesOnNeedAction?.length > 0) ||
      isConnectionRequestExist
    );
  }, [processedActivities]);

  const { isSearchOpened, isSearchOpenedSet } = useLayoutStore();

  const { query } = useRouter();

  useEffect(() => {
    const { globalSearch } = query;
    if (globalSearch === "true") {
      isSearchOpenedSet(true);
    }
  }, [query]);

  useEffect(() => {
    if (authUser?.bio?.user?.avatarUrl || authUser?.bio?.user?.firstName) {
      setAvatarUrl(
        authUser?.bio?.user?.avatarUrl || authUser?.bio?.user?.firstName || ""
      );
    }
  }, [authUser]);

  const { signOut } = useAuthenticator((context) => [context.signOut]);

  const logout = (): void => {
    handlerLogout(); // clears user from store and storage
    clearSentryUser();
    signOut();
    router.push({ pathname: PAGE_URL.login });
  };

  const items: MenuProps["items"] = [
    {
      key: "menu-profile",
      label: (
        <Link id="profile-link-element" href={PAGE_URL.profile}>
          Profile
        </Link>
      ),
    },
    {
      key: "menu-settings",
      label: <Link href={PAGE_URL.settings}>Settings</Link>,
    },
    {
      key: "action-logout",
      label: "Logout",
      onClick: () => {
        setAvatarUrl(""); // clear avatar state
        logout();
      },
    },
  ];

  const type_tier = useTypeTier(authUser?.bio?.user?.points);

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    const { value } = event.target as HTMLInputElement;
    if (!value && event?.key === "Enter") {
      return router.push("/dashboard")
    }
    if (value && event?.key === "Enter") {
      track("Global Search", "Input Search Keyword", value);

      router.push({
        pathname: PAGE_URL.search,
        query: { q: value },
      });
    }
  }

  return (
    <>
      <HeaderAntd
        style={{
          padding: 0,
          background: colorBgContainer,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          top: 0,
          zIndex: 99,
          width: "100%",
        }}
      >
        <BrandLogo className="block xl:hidden" />
        <div className="hidden items-center xl:flex">
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              padding: 0,
              fontSize: "1rem",
              width: 64,
              height: 64,
              border: 0,
            }}
          />
          <h1 className="text-xl text-white font-semibold">{title}</h1>
          {rightCta && <div className="ml-5">{rightCta}</div>}
        </div>
        <div className="lg:px-5 flex items-center lg:gap-6">
          <div className="hidden xl:flex items-center relative">
            <Input
              className="search-input-custom"
              placeholder="Search"
              bordered
              prefix={<SearchOutlined size={28} className="cursor-pointer" />}
              autoFocus
              onBlur={() => isSearchOpenedSet(false)}
              onKeyDown={onKeyDown}
              defaultValue={router?.query?.q}
              type="search"
              inputMode="search"
              enterKeyHint="search"
            />
          </div>
          <Link
            className="relative cursor-pointer xl:block"
            href={`${PAGE_URL.profile}?defaultTab=${myProfileTabKey.ACTIVITY}`}
          >
            <button className="text-gray-500 text-xl flex items-center">
              <HiBell />
            </button>

            {isNotificationExist ? (
              <span className="absolute flex h-2 w-2 right-0 top-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
              </span>
            ) : (
              ""
            )}
          </Link>
          <div className="hidden xl:block" id="header-avatar-element">
            <Tooltip
              title={`${authUser?.bio?.user?.firstName || ""} ${authUser?.bio?.user?.lastName || ""}`}
              placement="bottom"
            >
              <Dropdown
                menu={{ items }}
                placement="bottomRight"
                arrow
                trigger={["click"]}
                className="cursor-pointer"
              >
                <div className="flex gap-3 items-center">
                  <Avatar
                    size="xs"
                    alt="profile"
                    src={avatarUrl}
                    name={avatarUrl}
                    type_tier={type_tier as never}
                    styleTier={{
                      height: "inherit",
                    }}
                  />
                  <HiChevronDown className="text-sm text-gray-600" />
                </div>
              </Dropdown>
            </Tooltip>
          </div>
          <div className="block xl:hidden">
            <Button
              type="link"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                padding: 0,
                fontSize: "1rem",
                width: 64,
                height: 64,
                border: 0,
              }}
            />
          </div>
        </div>
      </HeaderAntd>
      <div className="flex xl:hidden items-center relative px-4 pt-4">
        <Input
          className="search-input-custom"
          placeholder="Search"
          bordered
          prefix={<SearchOutlined size={28} className="cursor-pointer" />}
          onBlur={() => isSearchOpenedSet(false)}
          onKeyDown={onKeyDown}
          defaultValue={router?.query?.q}
          type="search"
          inputMode="search"
          enterKeyHint="search"
        />
      </div>
    </>
  );
};

export default function Header() {
  const [headerKey, setHeaderKey] = useState(0);

  // This function will be passed to HeaderComponent and called on logout
  const forceRemount = () => setHeaderKey((prev) => prev + 1);

  return <HeaderComponent key={headerKey} forceRemount={forceRemount} />;
}
